import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { SpinoffAuthService } from '@sbe-spinoff-client/spinoff-platform';
// 백엔드 OAuthClientType(Prisma enum)을 openapi-spec → orval 파이프라인으로 그대로 가져와
// 이 파일 안에서도 쓰고(아래 인터페이스), 다른 컴포넌트에도 재노출한다 — 값을 손으로
// 재선언하지 않는다(docs/development/web-openapi-orval.md "API 자원은 Orval 생성물만
// 사용한다"). `export ... from`은 재노출만 할 뿐 이 파일 로컬 스코프에 바인딩을 만들지
// 않으므로, 로컬에서도 쓰려면 별도 import가 필요하다. 재생성: `nx run schoolbell-e-web:openapi-client`.
import {
  MemberLabelTargetType,
  MemberRole,
  type AssignOrgMemberLabelDto,
  type BatchUpdateOrgMemberLabelAssignmentsDto,
  type ConnectableServiceDto,
  type ConnectServiceBodyDto,
  type ConnectServiceResponseDto,
  type ConnectViaMagicLinkBodyDto,
  type ContactAssignmentDto,
  type ContinuityLinkDto,
  type CreateContinuityLinkDto,
  type CreateOrgMemberLabelDto,
  type CreateOrgTeamDto,
  type DisconnectServiceBodyDto,
  type BatchMemberOperationDto,
  type BatchMembersBodyDto,
  type BatchOrgTeamOperationDto,
  type BatchOrgTeamsBodyDto,
  type MagicLinkEntryResponseDto,
  type OrgMemberLabelRefDto,
  type MoveOrgTeamDto,
  type MyOrgListResponseDto,
  type InteractionDetailsDto,
  type InteractionRedirectDto,
  type LoginBodyDto,
  type OAuthConsentListResponseDto,
  type OAuthSubjectType,
  type OrgAuditLogItemDto,
  type OrgMemberLabelDto,
  type OrgMemberLabelListResponseDto,
  type OrgMemberDto,
  type OrgMemberListResponseDto,
  type ListMyOrgMembersResponseDto,
  type OrgTeamDetailDto,
  type OrgTeamDto,
  type OrgTeamListResponseDto,
  type PutAppLayerSettingDto,
  type SetMemberPositionsDto,
  type TeamServiceSettingDto,
  type UpdateOrgMemberLabelDto,
  type UpdateOrgTeamDto,
  type VisibleServiceDto,
} from '../../core/generated-api/schoolbell-e/models';
export type {
  ContactAssignmentDto,
  ContinuityLinkDto,
  CreateContinuityLinkDto,
  BatchMemberOperationDto,
  BatchMembersBodyDto,
  BatchOrgTeamOperationDto,
  BatchOrgTeamsBodyDto,
  OrgMemberLabelRefDto,
  OrgAuditLogItemDto,
  OrgMemberLabelDto,
  OrgMemberDto,
  OrgTeamDetailDto,
  OrgTeamDto,
  TeamServiceSettingDto,
  VisibleServiceDto,
};
export { MemberLabelTargetType, MemberRole };

// login.component.ts(소셜 로그인 버튼 3종)가 쓰는 provider 리터럴 유니언 — 단일 소스로
// 선언해 인라인 유니언 중복을 피한다(리뷰 반영, 2026-08-12). SpinoffAuthService.
// signInWithSocial()의 provider 파라미터는 대문자 유니언('Google'|'Apple'|'Kakao')이라
// 호출부에서 매핑이 필요하다(login.component.ts 참고).
export type SocialProvider = 'google' | 'apple' | 'kakao';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(SpinoffAuthService);

  // login/refresh/signup/social* 등 인증 관련 메서드는 SpinoffAuthService로 이전됨
  // (2026-08-17, spinoff-auth-pattern-alignment 플랜 PR3 — AuthLocalModule+SpinoffBffModule
  // 마운트). 이 서비스는 이제 그 외 schoolbell-e 고유 API(interaction/orgs/oauth/app-access
  // 등)만 담당한다 — authHeaders()는 여전히 이 파일의 나머지 메서드 전부가 인증에 쓴다.
  private authHeaders(): HttpHeaders {
    const token = this.auth.accessToken();
    return token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : new HttpHeaders();
  }

  private url(path: string): string {
    return `${environment.apiUrl}${path}`;
  }

  getInteractionDetails(uid: string) {
    return this.http.get<InteractionDetailsDto>(this.url(`/oauth/interaction/${uid}/details`), { withCredentials: true });
  }

  interactionLogin(uid: string, subjectType: OAuthSubjectType, subjectId: string) {
    const body: LoginBodyDto = { subjectType, subjectId };
    return this.http.post<InteractionRedirectDto>(
      this.url(`/oauth/interaction/${uid}/login`),
      body,
      { headers: this.authHeaders(), withCredentials: true },
    );
  }

  interactionDeny(uid: string) {
    return this.http.post<InteractionRedirectDto>(
      this.url(`/oauth/interaction/${uid}/deny`),
      {},
      { withCredentials: true },
    );
  }

  // 2026-07-06: 구 GET /auth/me/orgs → GET /me/orgs로 통합(OrgAccessGuard가 이 세션 토큰도
  // 받아준다). 응답 키가 orgs→items, 필드가 orgId→id로 바뀌어 org-context.service.ts에서 매핑한다.
  // adminOnly=true면 백엔드가 admin/owner으로 소속된 단체만 걸러 반환한다(OAuth 연동 단체
  // 선택처럼 admin 권한이 전제되는 화면 전용 — 헤더 드롭다운 등 일반 목록은 기본값 그대로 둔다).
  getMyOrgs(adminOnly?: boolean) {
    const params = adminOnly ? new HttpParams().set('adminOnly', 'true') : undefined;
    // 이전엔 { id, name } 형태를 직접 손으로 선언했었다 — Orval 생성물만 쓴다는 컨벤션
    // (docs/development/web-openapi-orval.md)에 맞춰 실제 백엔드 응답 형태(teams 포함)를
    // 그대로 반영하는 MyOrgListResponseDto로 교체했다(2026-08-18, 코드 리뷰 지적).
    return this.http.get<MyOrgListResponseDto>(
      this.url('/me/orgs'),
      { headers: this.authHeaders(), params },
    );
  }

  createOrg(name: string) {
    return this.http.post<{ id: string; name: string }>(
      this.url('/me/orgs'),
      { name },
      { headers: this.authHeaders() },
    );
  }

  getOrg(orgId: string) {
    return this.http.get<{ id: string; name: string; createdAt: string }>(
      this.url(`/orgs/${orgId}`),
      { headers: this.authHeaders() },
    );
  }

  // depth별로 엔드포인트가 분리되어 있다(2026-07-06 사용자 결정) — 기본은 orgId에 즉시 속한
  // 멤버만 flat하게 반환한다. 세션 인증에서는 org admin만 허용(org-members.service.ts#
  // listMembers) — admin이 아니면 403. org 직속/팀 소속 멤버가 같은 OrgMemberDto로 통합됐다
  // (2026-07-11, teamId로 구분).
  // options.labelIds를 주면 그 라벨(들) 중 하나라도 가진 멤버만 좁혀 받는다(백엔드는 이미
  // 지원 — list-org-members-query.dto.ts) — "멤버 라벨 관리"의 "라벨에 속하는 명단" 패널이
  // org 전체 명단 대신 이 필터로 좁혀 받는다. options.includeTeams를 true로 주면 org root뿐
  // 아니라 모든 하위 team의 멤버도 함께 반환한다(2026-08-18 — 같은 화면이 라벨 배정 대상을
  // "org+팀 구조 전체"에서 고르게 하기 위해, team-tree를 따로
  // 드릴다운하지 않고 한 번에 받는다). 정렬(올그+팀 구조 순서+role+번호)은 백엔드가
  // includeTeams=true일 때 이미 적용한다(org-members.service.ts#listMembers).
  // options.at(ISO 8601, 2026-08-19 GroupMember SCD-2 버저닝) — 주면 그 시점의 멤버 상태로
  // 재구성해 반환한다(listMembersAsOf). org-continuity.component.ts가 "이전 학기 자리" 검색에
  // 쓴다 — 백엔드가 at을 labelIds/memberIds/teamIds와 함께 주면 400으로 거부하므로
  // (list-org-members-query.dto.ts 참고) 이 옵션은 다른 필터와 함께 쓰지 않는다.
  getOrgMembers(orgId: string, options?: { labelIds?: string[]; includeTeams?: boolean; at?: string }) {
    let params = new HttpParams();
    if (options?.labelIds?.length) params = params.set('labelIds', options.labelIds.join(','));
    if (options?.includeTeams) params = params.set('includeTeams', 'true');
    if (options?.at) params = params.set('at', options.at);
    return this.http.get<OrgMemberListResponseDto>(
      this.url(`/orgs/${orgId}/members`),
      { headers: this.authHeaders(), params },
    );
  }

  // POST /orgs/:orgId/members/:groupMemberId/continuity-links —
  // org-member-continuity.controller.ts#createLink. sameAsMemberId(기존 멤버)를 주면
  // 그 continuity를 재사용/소급배정하고, 생략하면 새 연속성을 만든다. 이미 연결된 자리를
  // 다시 호출하면 정정(재연결)이다. orgAdminSessionOnlyGuard 전용(OAuth 토큰 불가) — 403은
  // 호출부가 안내 문구로 처리한다.
  createContinuityLink(orgId: string, groupMemberId: string, body: CreateContinuityLinkDto) {
    return this.http.post<ContinuityLinkDto>(
      this.url(`/orgs/${orgId}/members/${groupMemberId}/continuity-links`),
      body,
      { headers: this.authHeaders() },
    );
  }

  // teamId에 즉시 속한 멤버만 flat하게 반환한다(org-teams.service.ts#listTeamMembers).
  getTeamMembers(orgId: string, teamId: string) {
    return this.http.get<OrgMemberListResponseDto>(
      this.url(`/orgs/${orgId}/teams/${teamId}/members`),
      { headers: this.authHeaders() },
    );
  }

  // 팀 뷰용 단건(org-teams.controller.ts#getTeam) — 팀 이름·상위(parent)·즉시 하위(children)만
  // 반환한다. 이 팀의 모든 소속 구성원과 org admin/교직원이 호출할 수 있다(명단은 getTeamMembers).
  getOrgTeam(orgId: string, teamId: string) {
    return this.http.get<OrgTeamDetailDto>(
      this.url(`/orgs/${orgId}/teams/${teamId}`),
      { headers: this.authHeaders() },
    );
  }

  // 이 팀에서 내 자신의 자리(org-teams.controller.ts#listMyTeamMemberships) — 이 팀 소속이 아니면
  // 빈 배열. 본인 전용이라 admin 가드가 아니다.
  getMyTeamSeats(orgId: string, teamId: string) {
    return this.http.get<ListMyOrgMembersResponseDto>(
      this.url(`/me/orgs/${orgId}/teams/${teamId}/members`),
      { headers: this.authHeaders() },
    );
  }

  getOrgTeams(orgId: string) {
    return this.http.get<OrgTeamListResponseDto>(
      this.url(`/orgs/${orgId}/teams`),
      { headers: this.authHeaders() },
    );
  }

  // 팀(반) 생성·이름변경·삭제·이동+순서변경 — org-teams.controller.ts, 세션(org admin/교직원)
  // 또는 org.teams.write OAuth 둘 다 허용(2026-08-16 이후 현행 컨벤션, org-member-labels
  // 쓰기와 동일 인가 패턴).
  createOrgTeam(orgId: string, body: CreateOrgTeamDto) {
    return this.http.post<OrgTeamDto>(this.url(`/orgs/${orgId}/teams`), body, {
      headers: this.authHeaders(),
    });
  }

  renameOrgTeam(orgId: string, teamId: string, body: UpdateOrgTeamDto) {
    return this.http.patch<OrgTeamDto>(this.url(`/orgs/${orgId}/teams/${teamId}`), body, {
      headers: this.authHeaders(),
    });
  }

  // 하위 team·소속 구성원까지 함께 soft-delete된다(org-teams.service.ts#deleteTeam).
  deleteOrgTeam(orgId: string, teamId: string) {
    return this.http.delete<void>(this.url(`/orgs/${orgId}/teams/${teamId}`), {
      headers: this.authHeaders(),
    });
  }

  moveOrgTeam(orgId: string, teamId: string, body: MoveOrgTeamDto) {
    return this.http.patch<OrgTeamDto>(this.url(`/orgs/${orgId}/teams/${teamId}/position`), body, {
      headers: this.authHeaders(),
    });
  }

  // POST /orgs/:orgId/teams/batch — org-teams.controller.ts#batchWriteTeams
  // (org-teams.service.ts#batchWriteTeams). "학급구조" 화면의 일괄(batch) 저장 모드 전용
  // (2026-08-24) — createOrgTeam/renameOrgTeam/deleteOrgTeam/moveOrgTeam 네 조작을 한 번의
  // 트랜잭션으로 원자 처리한다. 개별 엔드포인트와 동일하게 세션(org admin/교직원) 또는
  // org.teams.write OAuth 둘 다 허용. 응답은 이 org의 팀 전체 최신 상태.
  batchOrgTeams(orgId: string, body: BatchOrgTeamsBodyDto) {
    return this.http.post<OrgTeamListResponseDto>(
      this.url(`/orgs/${orgId}/teams/batch`),
      body,
      { headers: this.authHeaders() },
    );
  }

  getConsents() {
    return this.http.get<OAuthConsentListResponseDto>(this.url('/oauth/consents'), { headers: this.authHeaders() });
  }

  revokeConsent(id: string) {
    return this.http.delete<void>(this.url(`/oauth/consents/${id}`), {
      headers: this.authHeaders(),
    });
  }

  getServiceConnections(orgId: string) {
    // orgId("{shard}:{id}")는 HttpParams 가 쿼리 값으로 인코딩한다.
    return this.http.get<ConnectableServiceDto[]>(this.url('/oauth/service-connections'), {
      headers: this.authHeaders(),
      params: new HttpParams().set('orgId', orgId),
    });
  }

  connectService(params: ConnectServiceBodyDto) {
    return this.http.post<ConnectServiceResponseDto>(this.url('/oauth/service-connections'), params, {
      headers: this.authHeaders(),
    });
  }

  // magic-link 핸드오프 — 서버가 user/org 토큰을 mint해 RP 콜백 URL(`redirectTo`)을 돌려준다.
  // 인가는 서버가 서비스의 노출 판정과 호출자의 수락한 자리로 정한다(단체 관리자 전용이 아니다).
  // `teamId`("{shard}:{id}")가 있으면 팀 단위 입장이다 — 서버가 그 팀 컨텍스트로 판정하고 토큰 `org.assignment`에 팀 경로를 싣는다.
  enterServiceViaMagicLink(params: ConnectViaMagicLinkBodyDto) {
    return this.http.post<MagicLinkEntryResponseDto>(this.url('/oauth/service-connections/magic-link'), params, {
      headers: this.authHeaders(),
    });
  }

  disconnectService(orgId: string, clientId: string) {
    const body: DisconnectServiceBodyDto = { orgId, clientId };
    return this.http.delete<void>(this.url('/oauth/service-connections'), {
      headers: this.authHeaders(),
      body,
    });
  }

  // 서비스 사용 결정(단체 관리자 세션 전용) — `features/service-usage`. 켤 때는 화면이 보여 준 위임 scope 목록을
  // `acknowledgedScopes`로 보내야 하고, 현재 목록과 다르면 409(`SCOPES_CHANGED`, 응답 본문 `scopes`에 현재 목록).
  getServiceUsage(orgId: string, appId: string) {
    return this.http.get<ServiceUsageDto>(this.url(`/orgs/${orgId}/apps/${appId}/usage`), {
      headers: this.authHeaders(),
    });
  }

  putServiceUsage(orgId: string, appId: string, body: { enabled: boolean; acknowledgedScopes?: string[] }) {
    return this.http.put<ServiceUsageDto>(this.url(`/orgs/${orgId}/apps/${appId}/usage`), body, {
      headers: this.authHeaders(),
    });
  }

  // 단체 단위 계층 설정 쓰기(순서·하위 팀 위임) — `features/app-layer-setting`. 생략한 필드는 유지, null 은 상속으로 되돌림.
  putAppLayerSetting(
    orgId: string,
    appId: string,
    body: {
      visible?: boolean | null;
      visibleDelegable?: boolean | null;
      sortKey?: string | null;
      sortDelegable?: boolean | null;
      applyToTeams?: 'keep' | 'overwrite';
    },
  ) {
    return this.http.put<unknown>(this.url(`/orgs/${orgId}/apps/${appId}/setting`), body, {
      headers: this.authHeaders(),
    });
  }

  // 팀 단위 서비스 노출·순서 설정 — `features/team-service-setting`(읽기)·`features/app-layer-setting`(쓰기). 읽기는 쓰기와 같은 가드라
  // 팀 관리자(와 단체·상위 팀 관리자)만 호출한다. 응답의 `visibleSetting`·`sortSetting`이 값별로 바꿀 수 있는지와 막힌 사유를 알려 준다.
  getTeamServiceSettings(orgId: string, teamId: string) {
    return this.http.get<TeamServiceSettingDto[]>(this.url(`/orgs/${orgId}/teams/${teamId}/service-settings`), {
      headers: this.authHeaders(),
    });
  }

  // 팀 행 쓰기 — 생략한 필드는 유지, null 은 상속으로 되돌림. 위 층이 위임하지 않은 값을 쓰면 서버가 403.
  putTeamAppLayerSetting(orgId: string, teamId: string, appId: string, body: PutAppLayerSettingDto) {
    return this.http.put<unknown>(this.url(`/orgs/${orgId}/teams/${teamId}/apps/${appId}/setting`), body, {
      headers: this.authHeaders(),
    });
  }

  // 이 단체에서 내가 볼 수 있는 서비스 목록(노출 판정을 통과한 것만, 유효 순서 정렬) — `features/magic-link-entry`.
  // 단체 관리자 전용인 `getServiceConnections` 와 달리 수락한 구성원 누구나 호출한다. 수락한 자리가 없으면 403.
  getVisibleServices(orgId: string) {
    return this.http.get<VisibleServiceDto[]>(this.url(`/orgs/${orgId}/services`), {
      headers: this.authHeaders(),
    });
  }

  // 이 팀 컨텍스트에서 내가 볼 수 있는 서비스 목록 — 팀 입장을 받는 서비스만, 팀 행의 숨김·위임을 반영한 유효 순서.
  // 그 팀에 들어갈 자격(팀 수락 좌석·단체 직속 관리자/교직원·상위 팀 담당자)이 없으면 403.
  getTeamVisibleServices(orgId: string, teamId: string) {
    return this.http.get<VisibleServiceDto[]>(this.url(`/orgs/${orgId}/teams/${teamId}/services`), {
      headers: this.authHeaders(),
    });
  }

  // org-member-labels.controller.ts — 커스텀 라벨 카탈로그 조회.
  getOrgLabels(orgId: string) {
    return this.http.get<OrgMemberLabelListResponseDto>(this.url(`/orgs/${orgId}/member-labels`), {
      headers: this.authHeaders(),
    });
  }

  // 단체 커스텀 라벨 카탈로그 CRUD + 배정, 서비스별 세부 권한(2026-07-25, org-labels lib
  // 이관) — 백엔드가 libs/backend/spinoff-platform/src/org-labels의 계약을 따르므로 orgId/id
  // 필드는 문자열이다(이 앱의 물리 id는 number지만 응답 JSON은 lib 계약대로 string).
  // 조회(getOrgLabels)는 위에 이미 있어 재선언하지 않는다.
  createOrgLabel(orgId: string, body: CreateOrgMemberLabelDto) {
    return this.http.post<OrgMemberLabelDto>(this.url(`/orgs/${orgId}/member-labels`), body, {
      headers: this.authHeaders(),
    });
  }

  renameOrgLabel(orgId: string, labelId: string, body: UpdateOrgMemberLabelDto) {
    return this.http.patch<OrgMemberLabelDto>(
      this.url(`/orgs/${orgId}/member-labels/${labelId}`),
      body,
      { headers: this.authHeaders() },
    );
  }

  deleteOrgLabel(orgId: string, labelId: string) {
    return this.http.delete<void>(this.url(`/orgs/${orgId}/member-labels/${labelId}`), {
      headers: this.authHeaders(),
    });
  }

  // targetId는 group_members row id(멤버/팀 구성원 목록의 OrgMemberDto.id 또는
  // ContactAssignmentDto.id)를 문자열로 담은 값이다.
  assignOrgLabel(orgId: string, labelId: string, body: AssignOrgMemberLabelDto) {
    return this.http.post<void>(
      this.url(`/orgs/${orgId}/member-labels/${labelId}/assignments`),
      body,
      { headers: this.authHeaders() },
    );
  }

  unassignOrgLabel(orgId: string, labelId: string, targetId: string) {
    return this.http.delete<void>(this.url(`/orgs/${orgId}/member-labels/${labelId}/assignments/${targetId}`), {
      headers: this.authHeaders(),
    });
  }

  // 배정 배치(2026-08-24 PR #2353 PR3) — 멤버 N명을 한 번에 추가/제거할 때 assign/unassign을
  // N번 개별 호출하는 대신 이 엔드포인트 하나로 보낸다. 서버가 사전 검증 후 단일 트랜잭션으로
  // 원자 반영하므로 부분 성공
  // 개념 자체가 없다 — 전체 성공 또는 전체 실패.
  batchUpdateOrgLabelAssignments(orgId: string, labelId: string, body: BatchUpdateOrgMemberLabelAssignmentsDto) {
    return this.http.post<void>(
      this.url(`/orgs/${orgId}/member-labels/${labelId}/assignments/batch`),
      body,
      { headers: this.authHeaders() },
    );
  }
}

// 서비스 사용 결정 — 서버 `ServiceUsageDto` 와 같은 모양. mode: optional 단체가 직접 고름 / removable 회사 기본 노출(끌 수 있음) / forced 끌 수 없음.
export type ServiceUsageMode = 'optional' | 'removable' | 'forced';

export interface ServiceUsageDelegationDto {
  visible: boolean;
  sort: boolean;
}

export interface ServiceUsageDto {
  appId: string;
  clientId: string;
  name: string;
  mode: ServiceUsageMode;
  enabled: boolean;
  canToggle: boolean;
  /** 켤 때 이 서비스에 위임되는 scope 전체 — 화면이 보여 주고 확인을 받아 `acknowledgedScopes`로 되돌려 준다. */
  scopes: string[];
  sortKey: string | null;
  sortEditable: boolean;
  teamDelegation: ServiceUsageDelegationDto;
  canDelegateToTeams: ServiceUsageDelegationDto;
}

